import { ArrowDown } from "lucide-react";

const SLUG = "outsourced-financial-management";

/**
 * The structure of the team, which is what separates this from a part-time CFO.
 *
 * Three roles on one spine: a Financial Manager leading, an Accountant behind
 * the reporting, and day-to-day processing underneath. The connector is drawn
 * with the same hairline the rest of the site uses.
 */
export const TeamStructure = ({ roles }) => (
  <section
    id="finance-team"
    data-testid={`${SLUG}-team`}
    className="scroll-mt-24 border-y border-border bg-[color:var(--tt-surface-2)] py-14 sm:py-20"
  >
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-4">
          <h2 className="text-[27px] font-bold leading-[1.15] tracking-[-0.015em] text-foreground sm:text-[34px]">
            A finance team built around your business
          </h2>
          <p className="mt-4 max-w-[46ch] text-[15px] leading-relaxed text-muted-foreground">
            Different levels of work sit with the right level of experience — with senior time
            focused on oversight and decisions, and day-to-day finance handled efficiently.
          </p>
          <p className="mt-5 rounded-lg border border-[color:var(--tt-teal-line)] bg-white p-4 text-[13px] leading-relaxed text-muted-foreground">
            The size of the team and the time allocated to each role are determined by your
            business requirements.
          </p>
        </div>

        <ol className="lg:col-span-8">
          {roles.map(({ title, text, icon: Icon }, index) => (
            <li key={title} data-testid={`${SLUG}-role-${index + 1}`}>
              <article className="tt-card flex gap-5 p-5 sm:p-6">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[color:var(--tt-accent)]/10 text-[color:var(--tt-accent)]">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.1em] text-foreground">
                    {title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
                </div>
              </article>

              {index < roles.length - 1 ? (
                <div className="flex items-center gap-3 py-3 pl-9" aria-hidden="true">
                  <ArrowDown className="h-4 w-4 text-[color:var(--tt-accent)]" />
                  <span className="h-px flex-1 bg-border" />
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      </div>
    </div>
  </section>
);
